import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { fetchReleaseSnapshot } from "./discogs.functions";
import {
  FREE_LIMIT_MESSAGE,
  assertCanAddOwned,
  getRemainingOwnedSlots,
  isRestrictedUser,
} from "./plan-limits.server";

export type AlbumStatus = "owned" | "wishlist";
export type Listing = "none" | "for_sale" | "for_trade";

export type CollectionItem = {
  id: string;
  discogs_id: string;
  title: string;
  artist: string;
  year: number | null;
  cover_url: string | null;
  genres: string[];
  condition: string | null;
  sleeve_condition: string | null;
  media_condition: string | null;
  notes: string | null;
  status: AlbumStatus;
  listing: Listing;
  paid_price: number | null;
  paid_currency: string | null;
  purchased_at: string | null;
  personal_notes: string | null;
  sale_price: number | null;
  sale_description: string | null;
  price_brl: number | null;
  trade_notes: string | null;
  created_at: string;
};

const SELECT = `id, discogs_id, title, artist, year, cover_url, genres,
  condition, sleeve_condition, media_condition, notes, status, listing,
  paid_price, paid_currency, purchased_at, personal_notes,
  sale_price, sale_description, price_brl, trade_notes, created_at`;

export const listMyAlbums = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { status?: AlbumStatus }) => i)
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("user_albums")
      .select(SELECT)
      .eq("user_id", context.userId)
      .order("position", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: false });
    if (data.status) q = q.eq("status", data.status);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return (rows ?? []) as unknown as CollectionItem[];
  });

export const listMyCollection = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("user_albums")
      .select(SELECT)
      .eq("user_id", context.userId)
      .eq("status", "owned")
      .order("position", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as CollectionItem[];
  });

export const listMyWishlist = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("user_albums")
      .select(SELECT)
      .eq("user_id", context.userId)
      .eq("status", "wishlist")
      .order("position", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as CollectionItem[];
  });


export const addAlbum = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (i: {
      discogsId: string;
      status: AlbumStatus;
      listing?: Listing;
      paid_price?: number | null;
      paid_currency?: string | null;
      purchased_at?: string | null;
      personal_notes?: string | null;
      sale_price?: number | null;
      sale_description?: string | null;
      condition?: string | null;
      sleeve_condition?: string | null;
      media_condition?: string | null;
      title?: string | null;
      artist?: string | null;
      year?: number | null;
      cover?: string | null;
      genres?: string[] | null;
    }) => i,
  )
  .handler(async ({ data, context }) => {
    // Enforce free plan limit for new "owned" additions
    if (data.status === "owned") {
      const { data: existing } = await context.supabase
        .from("user_albums")
        .select("id, status")
        .eq("user_id", context.userId)
        .eq("discogs_id", String(data.discogsId))
        .maybeSingle();
      const isNewOwned = !existing || (existing as { status: string }).status !== "owned";
      if (isNewOwned) await assertCanAddOwned(context.userId, 1);
    }
    // Anunciar (venda/troca) é recurso Pro para contas novas.
    if (data.listing === "for_sale" || data.listing === "for_trade") {
      if (await isRestrictedUser(context.userId)) {
        throw new Error("Anunciar discos para venda ou troca é um recurso do plano Pro.");
      }
    }
    const snap =
      data.title && data.artist
        ? {
            discogsId: data.discogsId,
            title: data.title,
            artist: data.artist,
            year: data.year ?? null,
            cover: data.cover ?? "",
            genres: data.genres ?? [],
          }
        : await fetchReleaseSnapshot(data.discogsId);
    const payload: Record<string, unknown> = {
      user_id: context.userId,
      discogs_id: String(snap.discogsId),
      title: snap.title,
      artist: snap.artist,
      year: snap.year,
      cover_url: snap.cover,
      genres: snap.genres,
      status: data.status,
      listing: data.listing ?? "none",
      paid_price: data.paid_price ?? null,
      paid_currency: data.paid_currency ?? "BRL",
      purchased_at: data.purchased_at ?? null,
      personal_notes: data.personal_notes ?? null,
      sale_price: data.sale_price ?? null,
      sale_description: data.sale_description ?? null,
      condition: data.condition ?? data.media_condition ?? null,
      sleeve_condition: data.sleeve_condition ?? null,
      media_condition: data.media_condition ?? data.condition ?? null,
      // back-compat mirrors for marketplace UI
      price_brl: data.sale_price ?? null,
      trade_notes: data.sale_description ?? null,
    };
    const { error } = await context.supabase
      .from("user_albums")
      .upsert(payload as never, { onConflict: "user_id,discogs_id" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const addToCollection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { discogsId: string }) => input)
  .handler(async ({ data, context }) => {
    const { data: existing } = await context.supabase
      .from("user_albums")
      .select("id, status")
      .eq("user_id", context.userId)
      .eq("discogs_id", String(data.discogsId))
      .maybeSingle();
    const isNewOwned = !existing || (existing as { status: string }).status !== "owned";
    if (isNewOwned) await assertCanAddOwned(context.userId, 1);
    const snap = await fetchReleaseSnapshot(data.discogsId);
    const { error } = await context.supabase.from("user_albums").upsert(
      {
        user_id: context.userId,
        discogs_id: String(snap.discogsId),
        title: snap.title,
        artist: snap.artist,
        year: snap.year,
        cover_url: snap.cover,
        genres: snap.genres,
        status: "owned",
      },
      { onConflict: "user_id,discogs_id" },
    );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const addToWishlist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { discogsId: string }) => input)
  .handler(async ({ data, context }) => {
    const snap = await fetchReleaseSnapshot(data.discogsId);
    const { error } = await context.supabase.from("user_albums").upsert(
      {
        user_id: context.userId,
        discogs_id: String(snap.discogsId),
        title: snap.title,
        artist: snap.artist,
        year: snap.year,
        cover_url: snap.cover,
        genres: snap.genres,
        status: "wishlist",
      },
      { onConflict: "user_id,discogs_id" },
    );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const updateAlbumDetails = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (i: {
      id: string;
      status?: AlbumStatus;
      listing?: Listing;
      paid_price?: number | null;
      paid_currency?: string | null;
      purchased_at?: string | null;
      personal_notes?: string | null;
      condition?: string | null;
      sleeve_condition?: string | null;
      media_condition?: string | null;
      sale_price?: number | null;
      sale_description?: string | null;
    }) => i,
  )
  .handler(async ({ data, context }) => {
    const patch: Record<string, unknown> = {};
    if (data.status !== undefined) patch.status = data.status;
    if (data.listing !== undefined) patch.listing = data.listing;
    if (data.paid_price !== undefined) patch.paid_price = data.paid_price;
    if (data.paid_currency !== undefined) patch.paid_currency = data.paid_currency;
    if (data.purchased_at !== undefined) patch.purchased_at = data.purchased_at;
    if (data.personal_notes !== undefined) patch.personal_notes = data.personal_notes;
    if (data.condition !== undefined) patch.condition = data.condition;
    if (data.sleeve_condition !== undefined) patch.sleeve_condition = data.sleeve_condition;
    if (data.media_condition !== undefined) {
      patch.media_condition = data.media_condition;
      // keep legacy single field in sync with the vinyl's state
      if (data.condition === undefined) patch.condition = data.media_condition;
    }
    if (data.sale_price !== undefined) {
      patch.sale_price = data.sale_price;
      patch.price_brl = data.sale_price;
    }
    if (data.sale_description !== undefined) {
      patch.sale_description = data.sale_description;
      patch.trade_notes = data.sale_description;
    }
    const { error } = await context.supabase
      .from("user_albums")
      .update(patch as never)
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// Kept for back-compat
export const setAlbumStatus = updateAlbumDetails;

export const removeAlbum = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { id: string }) => i)
  .handler(async ({ data, context }) => {
    const { data: deleted, error } = await context.supabase
      .from("user_albums")
      .delete()
      .eq("id", data.id)
      .eq("user_id", context.userId)
      .select("id");
    if (error) throw new Error(error.message);
    if (!deleted || deleted.length === 0) {
      throw new Error("Esse disco não está na sua coleção.");
    }
    return { ok: true };
  });

export const removeFromCollection = removeAlbum;
export const removeFromWishlist = removeAlbum;

export const getMyAlbumByDiscogs = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { discogsId: string }) => i)
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("user_albums")
      .select("id, status, listing, paid_price, purchased_at")
      .eq("user_id", context.userId)
      .eq("discogs_id", String(data.discogsId))
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (row ?? null) as {
      id: string;
      status: AlbumStatus;
      listing: Listing;
      paid_price: number | null;
      purchased_at: string | null;
    } | null;
  });

export type BulkImportRow = {
  discogsId: string;
  status?: AlbumStatus;
  sleeve_condition?: string | null;
  media_condition?: string | null;
  personal_notes?: string | null;
  purchased_at?: string | null;
  paid_price?: number | null;
};

export type BulkImportResult = {
  imported: number;
  skipped: number;
  failed: Array<{ discogsId: string; reason: string }>;
};

const CHUNK = 25;

export const bulkImportAlbums = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { rows: BulkImportRow[] }) => i)
  .handler(async ({ data, context }): Promise<BulkImportResult> => {
    const rows = (data.rows ?? []).filter((r) => r && r.discogsId);
    const result: BulkImportResult = { imported: 0, skipped: 0, failed: [] };

    // Enforce free plan limit: cap how many *new owned* rows can go in.
    let remainingOwnedSlots = await getRemainingOwnedSlots(context.userId);
    const existingIdsRes = await context.supabase
      .from("user_albums")
      .select("discogs_id, status")
      .eq("user_id", context.userId)
      .in(
        "discogs_id",
        rows.map((r) => String(r.discogsId)),
      );
    const alreadyOwned = new Set(
      ((existingIdsRes.data ?? []) as Array<{ discogs_id: string; status: string }>)
        .filter((r) => r.status === "owned")
        .map((r) => String(r.discogs_id)),
    );

    for (let i = 0; i < rows.length; i += CHUNK) {
      const chunk = rows.slice(i, i + CHUNK);
      const snaps = await Promise.all(
        chunk.map(async (row) => {
          try {
            const snap = await fetchReleaseSnapshot(row.discogsId);
            return { row, snap, error: null as string | null };
          } catch (e) {
            return { row, snap: null, error: (e as Error).message || "release não encontrado" };
          }
        }),
      );
      const payloads = snaps
        .filter((s) => s.snap)
        .map(({ row, snap }) => ({
          user_id: context.userId,
          discogs_id: String(snap!.discogsId),
          title: snap!.title,
          artist: snap!.artist,
          year: snap!.year,
          cover_url: snap!.cover,
          genres: snap!.genres,
          status: row.status ?? "owned",
          listing: "none",
          paid_price: row.paid_price ?? null,
          paid_currency: "BRL",
          purchased_at: row.purchased_at ?? null,
          personal_notes: row.personal_notes ?? null,
          condition: row.media_condition ?? null,
          sleeve_condition: row.sleeve_condition ?? null,
          media_condition: row.media_condition ?? null,
        }));
      for (const fail of snaps.filter((s) => !s.snap)) {
        result.failed.push({ discogsId: fail.row.discogsId, reason: fail.error || "erro" });
      }

      // Cap "owned" payloads to remaining free-plan slots. Wishlist rows pass through.
      const accepted: typeof payloads = [];
      for (const p of payloads) {
        if (p.status === "owned" && !alreadyOwned.has(p.discogs_id)) {
          if (remainingOwnedSlots <= 0) {
            result.failed.push({ discogsId: p.discogs_id, reason: FREE_LIMIT_MESSAGE });
            continue;
          }
          remainingOwnedSlots -= 1;
        }
        accepted.push(p);
      }

      if (accepted.length > 0) {
        const { error } = await context.supabase
          .from("user_albums")
          .upsert(accepted as never, { onConflict: "user_id,discogs_id" });
        if (error) {
          for (const p of accepted) {
            result.failed.push({ discogsId: p.discogs_id, reason: error.message });
          }
        } else {
          result.imported += accepted.length;
        }
      }
    }
    result.skipped = (data.rows?.length ?? 0) - rows.length;
    return result;
  });

